import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AuthProvider, useAuth } from '@core/auth/AuthProvider';
import AuthGate from '@core/auth/AuthGate';
import AppLayout from '@core/components/AppLayout';
import { PeriodProvider } from '@core/components/PeriodProvider';
import { ToastProvider } from '@core/components/Toast';
import RequirePermission from '@core/components/RequirePermission';
import { MastersProvider } from '@core/masters/MastersProvider';
import MastersPage from '@core/masters/MastersPage';
import ExpenseApplyPage from '@core/flow/ExpenseApplyPage';
import MyApplicationsPage from '@core/flow/MyApplicationsPage';
import ApprovalFlowPage from '@core/flow/ApprovalFlowPage';
import ReceiveMoneyPage from '@core/flow/ReceiveMoneyPage';
import CashPage from '@core/cash/CashPage';
import DepartmentPlPage from '@core/pl/DepartmentPlPage';
import CompanyPlPage from '@core/pl/CompanyPlPage';
import RevenueEntryPage from '@core/transactions/RevenueEntryPage';
import BudgetPage from '@core/budget/BudgetPage';
import RecurringCostsPage from '@core/recurring/RecurringCostsPage';
import TransactionListPage from '@core/transactions/TransactionListPage';
import CashMatrixPage from '@core/cash/CashMatrixPage';
import LedgerPage from '@core/ledger/LedgerPage';
import ReceiptPrintPage from '@core/flow/ReceiptPrintPage';
import ReportPage from '@core/flow/ReportPage';
import ReportConfirmPage from '@core/flow/ReportConfirmPage';
import NewsPage from '@core/flow/NewsPage';
import TodoPage from '@core/flow/TodoPage';

function AppRoutes() {
  const { user, can, canConfirm, flowRole } = useAuth();
  const isAdmin = can('master_admin');
  const seesHonbu =
    can('company_pl') ||
    canConfirm ||
    (can('department_pl') && (user?.department_ids.includes('honbu') ?? false));

  return (
    <Routes>
      {/* 受領書の印刷ページ(サイドバーなしの単独レイアウト) */}
      <Route path="/receipt/:id" element={<ReceiptPrintPage />} />
      <Route element={<AppLayout />}>
        <Route index element={<Navigate to="/apply" replace />} />
        {/* やること(■17 第1弾・2026-08-31): 担当ステップへのショートカット。全権限が使える */}
        <Route path="/todo" element={<TodoPage />} />
        <Route
          path="/transactions"
          element={
            <RequirePermission allowed={flowRole === 'accounting'}>
              <TransactionListPage />
            </RequirePermission>
          }
        />
        <Route path="/apply" element={<ExpenseApplyPage />} />
        {/* 経費申請(承認不要)=事後報告(2026-08-26 会議)。全権限が使用可 */}
        <Route path="/report" element={<ReportPage />} />
        <Route path="/mylist" element={<MyApplicationsPage />} />
        {/* お知らせ欄(通知センター・2026-08-26 会議) */}
        <Route path="/news" element={<NewsPage />} />
        <Route
          path="/revenue"
          element={
            <RequirePermission allowed={can('transaction_input')}>
              <RevenueEntryPage />
            </RequirePermission>
          }
        />
        {/* ステータス別のフロー作業ページ(2026-08-26 分割): 承認/支払い/受領/完了確認 */}
        <Route
          path="/approve"
          element={
            <RequirePermission allowed={flowRole === 'bucho' || flowRole === 'approver'}>
              <ApprovalFlowPage page="approve" />
            </RequirePermission>
          }
        />
        {/* 支払い・受領は全員(「自分の支払い」「自分の受領」があるため) */}
        <Route path="/pay" element={<ApprovalFlowPage page="pay" />} />
        {/* 受領=受け取ったお金 / 受領待ち=まだ受け取っていないお金(2026-10-15 作り直し) */}
        <Route path="/receive" element={<ReceiveMoneyPage mode="received" />} />
        <Route path="/receive-wait" element={<ReceiveMoneyPage mode="waiting" />} />
        {/* 経理確認(支払い済み申請・2026-08-27) */}
        <Route
          path="/acct-confirm"
          element={
            <RequirePermission allowed={flowRole === 'accounting' || isAdmin}>
              <ReportConfirmPage />
            </RequirePermission>
          }
        />
        <Route
          path="/confirm"
          element={
            <RequirePermission allowed={flowRole === 'approver'}>
              <ApprovalFlowPage page="confirm" />
            </RequirePermission>
          }
        />
        <Route
          path="/cf"
          element={
            <RequirePermission allowed={canConfirm}>
              <CashPage />
            </RequirePermission>
          }
        />
        <Route
          path="/ledger"
          element={
            <RequirePermission allowed={canConfirm || isAdmin}>
              <LedgerPage />
            </RequirePermission>
          }
        />
        <Route
          path="/cf/matrix"
          element={
            <RequirePermission allowed={canConfirm}>
              <CashMatrixPage />
            </RequirePermission>
          }
        />
        <Route
          path="/recurring"
          element={
            <RequirePermission allowed={canConfirm || isAdmin}>
              <RecurringCostsPage />
            </RequirePermission>
          }
        />
        <Route
          path="/budget"
          element={
            <RequirePermission allowed={can('company_pl') && isAdmin}>
              <BudgetPage />
            </RequirePermission>
          }
        />
        <Route
          path="/pl/dept"
          element={
            <RequirePermission allowed={flowRole === 'bucho' && (user?.manager_department_ids.length ?? 0) > 0}>
              <DepartmentPlPage departmentId={user?.manager_department_ids[0]} />
            </RequirePermission>
          }
        />
        <Route
          path="/pl/honbu"
          element={
            <RequirePermission allowed={seesHonbu}>
              <DepartmentPlPage />
            </RequirePermission>
          }
        />
        <Route
          path="/pl"
          element={
            <RequirePermission allowed={can('company_pl')}>
              <CompanyPlPage />
            </RequirePermission>
          }
        />
        <Route
          path="/master"
          element={
            <RequirePermission allowed={isAdmin}>
              <MastersPage />
            </RequirePermission>
          }
        />
        <Route path="*" element={<Navigate to="/apply" replace />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <ToastProvider>
        <PeriodProvider>
          <BrowserRouter>
            <AuthGate>
              <MastersProvider>
                <AppRoutes />
              </MastersProvider>
            </AuthGate>
          </BrowserRouter>
        </PeriodProvider>
      </ToastProvider>
    </AuthProvider>
  );
}
